
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { UserRole } from '@/types/user';
import { getFreeRoles } from '@/utils/rolePermissions';
import { roles } from '@/data/roleData';
import { ScrollArea } from '@/components/ui/scroll-area';

interface SubscriptionOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userType: 'supabase' | 'internal';
  action: 'downgrade' | 'cancel';
  currentRole?: UserRole;
}

const SubscriptionOptionsModal: React.FC<SubscriptionOptionsModalProps> = ({ 
  isOpen, 
  onClose, 
  userType,
  action,
  currentRole
}) => {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [confirmationText, setConfirmationText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Get all free account types for downgrade options, excluding current role and parent/child
  const getDowngradeOptions = () => {
    const freeRoleIds = getFreeRoles().filter(role => 
      role !== currentRole && 
      role !== 'parent' && 
      role !== 'child'
    );
    
    return freeRoleIds.map(roleId => {
      const roleData = roles.find(role => role.id === roleId);
      return {
        id: roleId,
        title: roleData?.title || roleId,
        price: 'Free',
        features: roleData?.features || []
      };
    });
  };

  const downgradePlans = getDowngradeOptions();

  const cancellationReasons = [
    'Too expensive',
    'Not using enough features',
    'Found a better alternative',
    'Temporary break',
    'Other'
  ];

  const handleDowngrade = () => {
    if (!selectedOption) {
      toast.error('Please select a plan to downgrade to');
      return;
    }
    
    setShowConfirmation(true);
  };

  const handleCancel = () => {
    if (!selectedOption) {
      toast.error('Please select a reason for cancellation');
      return;
    }
    
    setShowConfirmation(true);
  };

  const confirmAction = () => {
    const requiredText = action === 'cancel' ? 'CANCEL SUBSCRIPTION' : 'DOWNGRADE PLAN';
    
    if (confirmationText !== requiredText) {
      toast.error(`Please type "${requiredText}" to confirm`);
      return;
    }

    if (action === 'cancel') {
      toast.success('Subscription cancelled successfully. Access will continue until the end of your billing period.');
    } else {
      const selectedPlan = downgradePlans.find(plan => plan.id === selectedOption);
      toast.success(`Successfully downgraded to ${selectedPlan?.title}`);
    }
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl h-[80vh] bg-gray-900 border-gray-700 flex flex-col">
        <DialogHeader className="flex-shrink-0 pb-4">
          <DialogTitle className="text-white text-xl">
            {action === 'cancel' ? 'Cancel Subscription' : 'Downgrade Plan'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 min-h-0">
          <ScrollArea className="h-full pr-4">
            <div className="space-y-6">
              {!showConfirmation ? (
                <>
                  {/* Warning */}
                  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-yellow-400 font-medium mb-2">
                          {action === 'cancel' ? 'Before you cancel' : 'Before you downgrade'}
                        </h4>
                        <p className="text-gray-300 text-sm">
                          {action === 'cancel' 
                            ? 'You\'ll lose access to all premium features at the end of your current billing period. Your data will be preserved for 90 days in case you want to reactivate.'
                            : 'You\'ll lose access to premium features immediately. Some data may not be accessible in the lower tier plan.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {action === 'downgrade' ? (
                    /* Downgrade Options */
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4">Choose your new plan:</h3>
                      <div className="space-y-3">
                        {downgradePlans.map((plan) => (
                          <Card 
                            key={plan.id}
                            className={`bg-gray-800 border-gray-700 cursor-pointer transition-all ${
                              selectedOption === plan.id ? 'ring-2 ring-sports-green' : ''
                            }`}
                            onClick={() => setSelectedOption(plan.id)}
                          >
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-white">{plan.title}</CardTitle>
                                <Badge className="bg-sports-green text-black">{plan.price}</Badge>
                              </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <ul className="space-y-1 text-sm text-gray-300">
                                {plan.features.map((feature, index) => (
                                  <li key={index}>• {feature}</li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* Cancellation Reasons */
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4">Help us improve - why are you cancelling?</h3>
                      <div className="space-y-2">
                        {cancellationReasons.map((reason) => (
                          <div
                            key={reason}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              selectedOption === reason 
                                ? 'border-sports-green bg-sports-green/10' 
                                : 'border-gray-700 bg-gray-800 hover:bg-gray-750'
                            }`}
                            onClick={() => setSelectedOption(reason)}
                          >
                            <span className="text-white">{reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Confirmation Step */
                <div className="space-y-6">
                  <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-red-400 font-medium mb-2">Final Confirmation</h4>
                        <p className="text-gray-300 text-sm mb-4">
                          This action cannot be undone. Please type{' '}
                          <strong>"{action === 'cancel' ? 'CANCEL SUBSCRIPTION' : 'DOWNGRADE PLAN'}"</strong>{' '}
                          to confirm.
                        </p>
                        <input
                          type="text"
                          value={confirmationText}
                          onChange={(e) => setConfirmationText(e.target.value)}
                          className="w-full p-2 bg-gray-800 border border-gray-600 rounded text-white"
                          placeholder={action === 'cancel' ? 'CANCEL SUBSCRIPTION' : 'DOWNGRADE PLAN'}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                {userType === 'supabase' ? 'Old School account subscription' : 'New School account subscription'}
              </p>
            </div>
          </ScrollArea>
        </div>

        <div className="flex-shrink-0 pt-4 border-t border-gray-700">
          {!showConfirmation ? (
            <div className="flex gap-3">
              <Button
                onClick={action === 'cancel' ? handleCancel : handleDowngrade}
                className={action === 'cancel' 
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-sports-green text-black hover:bg-green-600'
                }
              >
                {action === 'cancel' ? 'Continue to Cancel' : 'Continue to Downgrade'}
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Keep Current Plan
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                onClick={confirmAction}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {action === 'cancel' ? 'Cancel Subscription' : 'Downgrade Plan'}
              </Button>
              <Button
                onClick={() => setShowConfirmation(false)}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Go Back
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionOptionsModal;
